import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import AssessmentIcon from "@mui/icons-material/Assessment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  useGetCurrentUserProfile,
  useGetSummary,
  useGetScores,
  useListConversations,
} from "../../api/generated/generated";
import type {
  ConversationResponse,
  DashboardSummary,
  ScoreAnalytics,
  UserProfile,
} from "../../api/generated";
import { MetricCard } from "../Analytics/components/MetricCard";
import { UsageChart } from "../Analytics/components/UsageChart";
import { PageHeader } from "../../components/common/PageHeader";
import { SectionCard } from "../../components/common/SectionCard";
import { EmptyState } from "../../components/common/EmptyState";
import { DashboardSkeleton } from "./components/DashboardSkeleton";
import { RecentActivity } from "./components/RecentActivity";
import { TeamOverview } from "./components/TeamOverview";
import { WelcomeCard } from "./components/WelcomeCard";
import hanoverLogo from "../../assets/hanover.jpg";

export function DashboardPage() {
  const { user } = useAuth();
  const { data: profileRes } = useGetCurrentUserProfile();
  const profile = profileRes?.data as UserProfile | undefined;
  const { data: summaryRes, isLoading: summaryLoading } = useGetSummary({
    period: "this_month",
  });
  const summary = summaryRes?.data as DashboardSummary | undefined;
  const { data: scoresRes } = useGetScores();
  const scores = scoresRes?.data as ScoreAnalytics | undefined;
  const { data: conversationsRes } = useListConversations({ limit: 5 });
  const conversations = conversationsRes?.data as
    | ConversationResponse[]
    | undefined;

  const isManager =
    user?.roles?.includes("sales_manager") ||
    user?.roles?.includes("org_owner") ||
    user?.superAdmin;

  if (summaryLoading) {
    return <DashboardSkeleton />;
  }

  const hasData =
    (conversations && conversations.length > 0) ||
    (summary &&
      summary.conversations?.total_count &&
      summary.conversations.total_count > 0);

  const convMetrics = summary?.conversations;
  const scoreMetrics = summary?.scores;

  return (
    <Box>
      <PageHeader
        title={`Welcome back, ${profile?.first_name || user?.email || "there"}`}
        subtitle="Here's how you're doing this month"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            component={Link}
            to="/chat"
          >
            New Conversation
          </Button>
        }
      />
      {!hasData ? (
        <WelcomeCard />
      ) : (
        <>
          {/* Metric Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard
                title="Pre-call Research"
                value={convMetrics?.pre_call_count ?? 0}
                change={convMetrics?.change_percent}
                icon={<SearchIcon />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard
                title="Call Analyses"
                value={convMetrics?.post_call_count ?? 0}
                change={convMetrics?.change_percent}
                icon={<AssessmentIcon />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard
                title="Average Score"
                value={scoreMetrics?.avg_overall ?? null}
                change={scoreMetrics?.change_percent}
                icon={<TrendingUpIcon />}
                format="score"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard
                title="Messages"
                value={convMetrics?.total_messages ?? 0}
                icon={<ChatBubbleIcon />}
              />
            </Grid>
          </Grid>

          {/* Score Trends + Recent Activity */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 8 }}>
              <SectionCard title="Score Trends">
                {scores?.trend && scores.trend.length > 0 ? (
                  <UsageChart data={scores.trend} />
                ) : (
                  <EmptyState
                    icon={<TrendingUpIcon />}
                    title="No score data yet"
                    description="Score trends will appear after your first call analysis."
                  />
                )}
              </SectionCard>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <SectionCard title="Recent Activity" sx={{ height: "100%" }}>
                <RecentActivity conversations={conversations ?? []} />
              </SectionCard>
            </Grid>
          </Grid>

          {/* Manager Team Overview */}
          {isManager && <TeamOverview summary={summary} />}
        </>
      )}
      {/* Powered-by footer */}
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          alignItems: "center",
          justifyContent: "center",
          mt: 6,
          mb: 2
        }}>
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Powered by
        </Typography>
        <Box
          component="img"
          src={hanoverLogo}
          alt="Hanover Learning"
          sx={{ height: 32, width: "auto", objectFit: "contain" }}
        />
      </Stack>
    </Box>
  );
}
